import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import FulfillmentTaskContent from '../../components/orders/FulfillmentTaskContent';
import { canOpenFulfillmentStep, earliestFulfillmentStep } from '../../components/orders/WorkflowStageRail';
import { businessFulfillmentService } from '../../services/businessFulfillmentService';
import { fulfillmentMutationError } from '../../utils/fulfillmentTaskErrors';
import { orderAttention } from '../../utils/orderAttention';
import { PageHeader } from '../../../components/ui';

const TITLES = { pack: 'Pack Order', label: 'Label & Submit for Dispatch', handoff: 'Rider Handoff' };
const OFF_PATH_TITLES = { action: 'Needs attention', waiting: 'Waiting for payment', settled: 'No fulfillment work left' };

export default function FulfillmentTaskPage({ step = null }) {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [packingDirty, setPackingDirty] = useState(false);
  const [discardWarning, setDiscardWarning] = useState(false);
  const navigate = useNavigate();
  const requestRef = useRef(0);
  const backQuery = params.toString();
  const backTo = `/business/orders/fulfillment${backQuery ? `?${backQuery}` : ''}`;

  const load = useCallback(async ({ quiet = false } = {}) => {
    const requestId = ++requestRef.current;
    if (!quiet) setLoading(true);
    setError('');
    try {
      const next = await businessFulfillmentService.detail(orderId);
      if (requestId === requestRef.current) setOrder(next);
    } catch {
      if (requestId === requestRef.current) setError('Could not load this order. Check your connection and try again.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
    return () => { requestRef.current += 1; };
  }, [load]);

  // Automatic dispatch runs seconds after the label is confirmed, but this page
  // loaded once and kept saying "Finding a rider" until reloaded - it looked as if
  // dispatch never happened. While the parcel waits for a rider, check quietly
  // every few seconds and when the tab comes back into view.
  // Never while packing: a refresh would replace quantities being typed.
  const awaitingRider = step !== 'pack' && order?.readiness?.status === 'READY' && !order?.delivery?.riderName;
  useEffect(() => {
    if (!awaitingRider) return undefined;
    const refresh = () => { if (document.visibilityState === 'visible') load({ quiet: true }); };
    const timer = window.setInterval(refresh, 5000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [awaitingRider, load]);

  useEffect(() => {
    const warn = event => {
      if (!packingDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [packingDirty]);

  const runMutation = async (work, message) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const result = await work();
      setSuccess(typeof message === 'function' ? message(result) : message);
      await load({ quiet: true });
      return result;
    } catch (nextError) {
      setError(fulfillmentMutationError(nextError));
    } finally {
      setBusy(false);
    }
  };

  const save = items => runMutation(() => businessFulfillmentService.saveChecklist(orderId, items), 'Packing progress saved.');
  // `checklist` (MARK_PACKED only): quantities ticked but not yet saved. They
  // are saved first, then the parcel is marked packed; saving touches only the
  // item versions, not the order's status version.
  const act = async (action, checklist = null) => {
    const command = { START_PACKING: 'start-processing', MARK_PACKED: 'mark-packed' }[action];
    if (!command) return;
    const result = await runMutation(async () => {
      if (action === 'MARK_PACKED' && checklist) await businessFulfillmentService.saveChecklist(orderId, checklist);
      return businessFulfillmentService.transition(orderId, command, order.statusVersion);
    }, {
      START_PACKING: 'Packing started. Confirm each physical quantity as you work.',
      MARK_PACKED: 'Packing completed and quantities locked. Next: generate and attach the label.',
    }[action]);
    if (result && action === 'MARK_PACKED') navigate(`/business/orders/fulfillment/${orderId}/label${backQuery ? `?${backQuery}` : ''}`);
  };
  const parcelCommand = async (command, payload) => {
    const result = await runMutation(
    () => businessFulfillmentService.parcelCommand(order.businessId, order.shipmentId, command, payload),
    result => {
      const ready = result?.readiness?.status === 'READY';
      return {
        ISSUE: 'Label generated. Print it, attach it to the parcel, then confirm it is attached.',
        CONFIRM_ATTACHED: ready
          ? 'Label confirmed attached. The parcel passed its checks and was sent to dispatch; a Rider will be assigned automatically.'
          : 'Label confirmed attached, but the parcel can’t be dispatched. See why below.',
        REPRINT: 'Controlled reprint recorded.',
        REPLACE: 'Former label retired and replacement generated. Print the new label, attach it, then confirm it is attached.',
      }[command] || 'Parcel record updated.';
    },
    );
    // No redirect: printing, attaching and confirming all happen on this page.
    // The dispatch card links on to the handoff once the parcel is ready.
    return result;
  };

  if (order) {
    const target = earliestFulfillmentStep(order);
    if (!step || !canOpenFulfillmentStep(order, step)) {
      return <Navigate replace to={`/business/orders/fulfillment/${orderId}/${target}${backQuery ? `?${backQuery}` : ''}`} />;
    }
  }

  const attention = orderAttention(order);
  const offPath = Boolean(attention?.blocksWorkflow);

  return <main className="min-w-0 space-y-5 text-neutral-950 dark:text-white">
    <Link to={backTo} onClick={event => { if (packingDirty) { event.preventDefault(); setDiscardWarning(true); } }} className="inline-flex min-h-11 items-center gap-2 rounded-lg text-sm font-semibold text-secondary hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to fulfillment queue</Link>
    <PageHeader
      title={offPath ? OFF_PATH_TITLES[attention.kind] : TITLES[step] || 'Fulfillment task'}
      description={`${order?.orderReference ? `${order.orderReference} · ` : ''}${offPath ? 'This order is not on the packing path right now. The reason and the next step are below.' : 'Complete this step, then continue. Finished steps remain available for review.'}`}
    />

    {loading && !order ? <div role="status" className="grid min-h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" /><span className="sr-only">Loading fulfillment details…</span></div> : null}
    {error ? <div role="alert" className="rounded-xl border border-danger bg-danger-subtle p-4 text-sm text-danger"><p>{error}</p>{!order ? <button type="button" onClick={() => load()} className="mt-3 min-h-11 rounded-xl border border-danger px-4 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Try loading again</button> : null}</div> : null}
    {discardWarning ? <section role="alert" className="rounded-xl border border-warning bg-warning-subtle p-4 text-warning"><p className="font-semibold">Discard unsaved packing progress?</p><p className="mt-1 text-sm">Your changed quantities have not been saved to this order.</p><div className="mt-3 flex flex-col gap-2 sm:flex-row"><button type="button" onClick={() => setDiscardWarning(false)} className="min-h-11 rounded-xl border border-warning px-4 font-semibold">Keep editing</button><Link to={backTo} className="inline-flex min-h-11 items-center justify-center rounded-xl bg-danger px-4 font-semibold text-on-accent">Discard changes</Link></div></section> : null}
    {order ? <FulfillmentTaskContent order={order} step={step} query={backQuery} busy={busy} success={success} onSave={save} onAction={act} onParcelCommand={parcelCommand} onPackingDirtyChange={setPackingDirty} /> : null}
  </main>;
}
